import React, { type FC, type PropsWithChildren } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace, NAMESPACE_LIVE, NAMESPACE_PREMATCH } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { SlotType } from 'app/lib/betting/betting-entities';
import { SportId } from 'app/lib/betting/constants';
import { ROUTE_PATH } from 'app/lib/routing/constants';
import { composeMatchCenterPath, getPrefixBySlotType } from 'app/modules/Navigation/utils';

import { getDataGaEventByNamespace } from '../utils/data-ga-event-by-namespace';

// ---

type StoreProps = {
  slotType: SlotType;
  namespace: ContextNamespace;
  sportId: SportId;
  tournamentUnionId?: number;
  tournamentId?: number;
  tournamentStageId?: number;
};

type MatchCenterLinkProps = {
  matchId: number;
  className?: string;
} & PropsWithChildren;

const MatchCenterLink: FC<MatchCenterLinkProps & StoreProps> = ({
  slotType,
  namespace,
  sportId,
  tournamentUnionId,
  tournamentId,
  tournamentStageId,
  matchId,
  className,
  children,
}) => {
  const { pathname, search } = useLocation();

  if (!sportId) {
    return <span className={className}>{children}</span>;
  }

  const isInFavoritePage = pathname === ROUTE_PATH.FAVORITES;
  const namespaceWithFavorites = isInFavoritePage ? ContextNamespace.FAVORITES : namespace;
  const dataGaEvent = getDataGaEventByNamespace(namespaceWithFavorites, 'detailView');

  const matchCenterPath = composeMatchCenterPath(
    { sportId, tournamentUnionId, tournamentId, tournamentStageId, matchId },
    getPrefixBySlotType(slotType),
  );

  return (
    <Link
      to={`${matchCenterPath}${search}`}
      className={className}
      data-ga-event={dataGaEvent}
      data-ga-sport-match={matchId}
      data-test={`match-allBetsLink-${matchId}`}>
      {children}
    </Link>
  );
};

const mapStateToProps = (
  state: ReduxState,
  {
    $betting: {
      namespace,
      content: {
        selectors: { matches },
      },
    },
    matchId,
  }: BettingContextProps,
): StoreProps => {
  const match = matches(state).get(matchId);

  let slotType;

  switch (namespace) {
    case NAMESPACE_LIVE:
      slotType = SlotType.LIVE;
      break;

    case NAMESPACE_PREMATCH:
      slotType = SlotType.PREMATCH;
      break;

    default:
      slotType = match.slotType;
      break;
  }

  return {
    slotType,
    namespace,
    sportId: match.sportId,
    tournamentUnionId: match?.tournamentUnionId,
    tournamentId: match?.tournamentId,
    tournamentStageId: match?.tournamentStageId,
  };
};

export default compose<FC<MatchCenterLinkProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(MatchCenterLink);
